import { useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useToast, useConfirm } from '@/components/ui';
import { api } from '@/lib/api';

interface MeishiScan {
  id: string;
  imageUrl: string;
  ocrText: string | null;
  extracted: {
    familyName?: string;
    givenName?: string;
    familyNameKana?: string;
    givenNameKana?: string;
    title?: string;
    department?: string;
    company?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    address?: string;
  };
  ocrEngine: string | null;
  confidence: number | null;
  status: string;
  contactId?: string | null;
}

export function MeishiPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const navigate = useNavigate();
  const [scan, setScan] = useState<MeishiScan | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Click a Recent-scan card:
  //  - confirmed (a contact was created)  → jump to /people (the user
  //    can see the row that came out of this scan).
  //  - review / failed                    → reopen in the editable
  //    form so the user can fix fields and re-save.
  function openScan(s: MeishiScan) {
    if (s.contactId) {
      navigate(`/app/workspace/${wsId}/people`);
      return;
    }
    setScan(s);
  }

  // Recent scans for this workspace — gives the user a visible audit
  // trail of what they've uploaded, with status + the contact they
  // were turned into.
  const recent = useQuery({
    queryKey: ['meishi', wsId, 'recent'],
    queryFn: async () => {
      const r = await api.get<{ items: MeishiScan[]; total: number }>(
        '/api/meishi',
        { params: { workspaceId: wsId, limit: 12 } },
      );
      return r.data.items;
    },
    enabled: !!wsId,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('workspaceId', wsId);
      const r = await api.post<MeishiScan>('/api/meishi/upload', fd);
      return r.data;
    },
    onSuccess: (data) => {
      setScan(data);
      qc.invalidateQueries({ queryKey: ['meishi', wsId, 'recent'] });
    },
    onError: () =>
      toast.error(t('meishi.upload_failed', { defaultValue: 'Could not read that card. Try a clearer photo.' })),
  });

  const confirm = useMutation({
    mutationFn: async (fields: MeishiScan['extracted']) => {
      const r = await api.post<{ contactId: string }>(`/api/meishi/${scan!.id}/confirm`, { fields });
      return r.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contacts', wsId] });
      qc.invalidateQueries({ queryKey: ['meishi', wsId, 'recent'] });
      setScan(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success(
        <span>
          {t('meishi.saved_toast', { defaultValue: 'Saved as contact.' })}{' '}
          <Link
            to={`/app/workspace/${wsId}/people`}
            className="underline font-medium"
          >
            {t('meishi.view_on_people', { defaultValue: 'View on People' })}
          </Link>
        </span>,
      );
    },
    onError: () =>
      toast.error(t('meishi.save_failed', { defaultValue: 'Could not save contact. Please retry.' })),
  });

  const removeScan = useMutation({
    mutationFn: (id: string) => api.delete(`/api/meishi/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meishi', wsId, 'recent'] });
      toast.success(t('meishi.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  async function askDeleteScan(id: string) {
    const ok = await confirmDialog({
      title: t('meishi.confirm_delete_title'),
      description: t('meishi.confirm_delete_body'),
      confirmLabel: t('meishi.delete'),
      variant: 'danger',
    });
    if (ok) removeScan.mutate(id);
  }

  return (
    <AppShell titleKey="meishi.title">
      <p className="text-sm text-ink-600 max-w-2xl mb-6">{t('meishi.intro')}</p>

      {!scan && (
        <div className="rounded-2xl border-2 border-dashed border-ink-300 p-10 text-center">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="inline-flex items-center px-5 py-3 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
          >
            {upload.isPending ? t('meishi.uploading') : t('meishi.upload_button')}
          </button>
          <p className="text-xs text-ink-500 mt-3">{t('meishi.upload_hint')}</p>
        </div>
      )}

      {scan && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <img src={scan.imageUrl} alt="" className="w-full rounded-xl border border-ink-200" />
            <p className="mt-3 text-xs text-ink-500">
              {t('meishi.ocr_engine_label')} <span className="num">{scan.ocrEngine || '—'}</span>{' '}
              · {t('meishi.confidence_label')}{' '}
              <span className="num">{scan.confidence ?? '—'}</span>
            </p>
          </div>
          <div>
            <EditableFields
              fields={scan.extracted}
              onConfirm={(f) => confirm.mutate(f)}
              busy={confirm.isPending}
            />
          </div>
        </div>
      )}

      {/* Recent scans — what's been uploaded to this workspace */}
      {!scan && (recent.data?.length ?? 0) > 0 && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">
            {t('meishi.recent_section', { defaultValue: 'Recent scans' })}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(recent.data ?? []).map((s) => {
              const name =
                [s.extracted.familyName, s.extracted.givenName]
                  .filter(Boolean)
                  .join(' ') ||
                s.extracted.email ||
                s.extracted.company ||
                t('meishi.recent_unnamed', { defaultValue: '(no name extracted)' });
              const isLinked = !!s.contactId;
              return (
                <li key={s.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => askDeleteScan(s.id)}
                    aria-label={t('meishi.delete')}
                    title={t('meishi.delete')}
                    className="absolute top-1.5 right-1.5 z-10 rounded-md p-1 text-ink-400 bg-white/80 opacity-0 group-hover:opacity-100 hover:bg-warn-500/10 hover:text-warn-600 transition-opacity"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openScan(s)}
                    className="flex w-full items-center gap-3 rounded-xl border border-ink-200 bg-white p-3 text-left transition hover:border-ink-400 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                    title={
                      isLinked
                        ? t('meishi.card_action_view_contact', {
                            defaultValue: 'View saved contact',
                          })
                        : t('meishi.card_action_review', {
                            defaultValue: 'Re-open for review',
                          })
                    }
                  >
                    <img
                      src={s.imageUrl}
                      alt=""
                      className="h-12 w-16 rounded-md object-cover border border-ink-100"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-900 truncate">
                        {name}
                      </div>
                      <div className="text-[11px] text-ink-500 truncate">
                        <span className="num">{s.ocrEngine || '—'}</span> ·{' '}
                        {t(`meishi.status_${s.status}`, { defaultValue: s.status })}
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-ink-500">
                      {isLinked
                        ? t('meishi.card_action_view_contact_short', {
                            defaultValue: 'View →',
                          })
                        : t('meishi.card_action_review_short', {
                            defaultValue: 'Edit →',
                          })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </AppShell>
  );
}

function EditableFields({
  fields,
  onConfirm,
  busy,
}: {
  fields: MeishiScan['extracted'];
  onConfirm: (f: MeishiScan['extracted']) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState(fields);
  const FIELDS: Array<{ key: keyof MeishiScan['extracted']; labelKey: string }> = [
    { key: 'familyName', labelKey: 'meishi.field_family_name' },
    { key: 'givenName', labelKey: 'meishi.field_given_name' },
    { key: 'familyNameKana', labelKey: 'meishi.field_family_name_kana' },
    { key: 'givenNameKana', labelKey: 'meishi.field_given_name_kana' },
    { key: 'company', labelKey: 'meishi.field_company' },
    { key: 'title', labelKey: 'meishi.field_title' },
    { key: 'department', labelKey: 'meishi.field_department' },
    { key: 'email', labelKey: 'meishi.field_email' },
    { key: 'phone', labelKey: 'meishi.field_phone' },
    { key: 'mobile', labelKey: 'meishi.field_mobile' },
    { key: 'address', labelKey: 'meishi.field_address' },
  ];
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(({ key, labelKey }) => (
          <label key={key} className="block">
            <span className="text-xs text-ink-500">{t(labelKey)}</span>
            <input
              type="text"
              value={(state[key] as string) || ''}
              onChange={(e) => setState({ ...state, [key]: e.target.value })}
              className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </label>
        ))}
      </div>
      <button
        onClick={() => onConfirm(state)}
        disabled={busy}
        className="mt-5 inline-flex items-center px-5 py-2.5 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
      >
        {busy ? t('meishi.saving') : t('meishi.save_button')}
      </button>
    </div>
  );
}
